import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { complianceApi, type EInvoiceIRNDetails } from '@/api/compliance.api';
import { useToast } from '@/contexts/ToastContext';

interface IrnLookupModalProps {
  onClose: () => void;
  initialIrn?: string;
}

function formatINR(n?: number | null): string {
  if (n == null || isNaN(Number(n))) return '₹0.00';
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function IrnLookupModal({ onClose, initialIrn = '' }: IrnLookupModalProps) {
  const navigate = useNavigate();
  const toast = useToast();

  const [irn, setIrn] = useState(initialIrn.trim());
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<EInvoiceIRNDetails | null>(null);
  const [error, setError] = useState<{ message: string; needsAuth: boolean } | null>(null);

  const handleLookup = async () => {
    const clean = irn.trim();
    if (clean.length !== 64) {
      toast.error('Enter a valid 64-character IRN');
      return;
    }

    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const res = await complianceApi.lookupEInvoiceIrn(clean);
      if (res?.success && res.data) {
        setResult(res.data);
      } else {
        setError({
          message: res?.message || 'IRN lookup failed',
          needsAuth: Boolean(res?.authRequired) || res?.errorCode === 'AUTH_REQUIRED',
        });
      }
    } catch (err: any) {
      setError({ message: err?.message || 'IRN lookup failed', needsAuth: false });
    } finally {
      setLoading(false);
    }
  };

  // Auto-run when opened with a pre-filled IRN (e.g. "Verify with NIC" from the detail view)
  useEffect(() => {
    if (initialIrn.trim().length === 64) {
      handleLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inv = result?.decodedInvoice;
  const isActive = result?.status === 'ACT';
  const statusDrift = result?.local && result.local.irpStatus && result.local.irpStatus !== result.status;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative animate-in fade-in zoom-in-95 duration-150 border border-slate-100">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl" />
        </button>

        <div className="w-10 h-10 rounded-full bg-indigo-50 text-[#4f46e5] flex items-center justify-center mb-3">
          <i className="ri-file-search-line text-xl" />
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-0.5">IRN Lookup</h3>
        <p className="text-xs text-gray-500 mb-4">
          Re-fetch a previously generated e-Invoice's live status directly from NIC. Only works for IRNs generated within the last 72 hours.
        </p>

        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={irn}
            onChange={(e) => setIrn(e.target.value.trim())}
            onKeyDown={(e) => e.key === 'Enter' && handleLookup()}
            maxLength={64}
            placeholder="64-character IRN"
            className="flex-1 px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
          <button
            onClick={handleLookup}
            disabled={loading || irn.trim().length !== 64}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {loading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-search-line" />}
            Lookup
          </button>
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 space-y-2">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
              <span>{error.message}</span>
            </div>
            {error.needsAuth && (
              <button
                onClick={() => navigate('/compliance/e-invoice/settings')}
                className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 underline hover:text-red-800 cursor-pointer"
              >
                Go to E-Invoice Settings to authenticate
              </button>
            )}
          </div>
        )}

        {result && (
          <div className="space-y-3">
            {statusDrift && (
              <div className="flex items-start gap-1.5 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-xs">
                <i className="ri-alert-line shrink-0 mt-0.5" />
                <span>
                  Status mismatch: NIC shows <strong>{result.status === 'ACT' ? 'Active' : 'Cancelled'}</strong>, but this record locally shows{' '}
                  <strong>{result.local?.irpStatus === 'ACT' ? 'Active' : 'Cancelled'}</strong>. NIC is the source of truth.
                </span>
              </div>
            )}

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="p-3.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <span className="text-sm font-bold text-gray-900">
                  {inv?.SellerDtls?.LglNm || '—'} → {inv?.BuyerDtls?.LglNm || '—'}
                </span>
                <span
                  className={`px-2 py-0.5 text-[10px] font-extrabold uppercase rounded ${
                    isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                  }`}
                >
                  {isActive ? 'Active on NIC' : 'Cancelled on NIC'}
                </span>
              </div>

              <div className="p-3.5 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Ack No.</span>
                  <span className="font-mono font-semibold text-gray-800">{result.ackNo || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Ack Date</span>
                  <span className="font-semibold text-gray-800">{result.ackDate || '—'}</span>
                </div>
                {inv?.DocDtls && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Document No.</span>
                    <span className="font-semibold text-gray-800">{inv.DocDtls.No} ({inv.DocDtls.Dt})</span>
                  </div>
                )}
                {inv?.ValDtls && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Invoice Value</span>
                    <span className="font-bold text-gray-900">{formatINR(inv.ValDtls.TotInvVal)}</span>
                  </div>
                )}
                {result.ewbNo && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-slate-500">E-Way Bill No.</span>
                      <span className="font-mono font-semibold text-gray-800">{result.ewbNo}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">EWB Valid Till</span>
                      <span className="font-semibold text-gray-800">{result.ewbValidTill || '—'}</span>
                    </div>
                  </>
                )}
                {result.remarks && (
                  <div className="flex justify-between gap-3">
                    <span className="text-slate-500 shrink-0">Remarks</span>
                    <span className="font-semibold text-gray-800 text-right">{result.remarks}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <button
          onClick={onClose}
          className="mt-4 w-full py-1.5 text-xs font-medium text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap"
        >
          Close
        </button>
      </div>
    </div>
  );
}
